import React, { useEffect, useMemo, useState } from "react";
import {
  FormControl,
  InputGroup,
  Button,
  Row,
  Col,
  Form,
  Table,
  Modal,
} from "react-bootstrap";
import ClearIcon from "@mui/icons-material/Clear";

import ApiManager from "../utils/ApiManager"; // Adjust the path as per your directory structure
import SearchIcon from "@mui/icons-material/Search";
import TodayIcon from "@mui/icons-material/Today";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import DataManager from "../utils/DataManager";
import RowCountLabel from "./RowCountLabel";
import ProductLink from "./ProductLink";
import ModalEditOutgoings from "./ModalEditOutgoings";

const PickingLists = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [triggerSetToday, setTriggerSetToday] = useState(false);
  const [data, setData] = useState([]);
  const [firstLoading, setFirstLoading] = useState(true);
  const [movReason, setMovReason] = useState(-1);
  const [showModalEditOutgoings, setShowModalEditOutgoings] = useState(false);
  const [selectedPickingListId, setSelectedPickingListId] = useState(0);
  const [pickingListIdToDelete, setPickingListIdToDelete] = useState(0);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false); // State for delete confirmation modal

  const fetchData = async () => {
    if (startDate && endDate) {
      await ApiManager.Top.pickingLists(
        { fromDate: startDate, toDate: endDate, movReason: movReason },
        (data) => {
          setData(data.pickingLists);
        },
        (error) => {
          console.error("Error fetching products:", error);
        },
        () => {}
      );
    }
  };

  useEffect(() => {
    setToday();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (
        (firstLoading === true || triggerSetToday) &&
        startDate &&
        endDate &&
        !data?.length
      ) {
        await fetchData(); // Wait for the fetchData to complete
        setTriggerSetToday(false);
        setFirstLoading(false);
      } else if (firstLoading === false && data?.length > 0) {
        setData([]);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, movReason, triggerSetToday]);

  useEffect(() => {
    setShowModalEditOutgoings(selectedPickingListId && movReason === -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPickingListId]);

  const setToday = () => {
    let now = DataManager.Date.toIso(new Date());
    setStartDate(now);
    setEndDate(now);
    setTriggerSetToday(true);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  const handleClearStartDate = (e) => {
    setStartDate("");
  };

  const handleClearEndDate = (e) => {
    setEndDate("");
  };

  const handleSearchClick = () => {
    fetchData();
  };

  const handleSameDate = (e) => {
    setEndDate(startDate);
  };

  const handleEditModal = (id) => {
    setSelectedPickingListId(id);
  };

  const handleDeletePickingList = (id) => {
    setPickingListIdToDelete(id);
    setShowDeleteConfirmation(true); // Show the delete confirmation modal
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchClick();
    }
  };

  const confirmDeletePickingList = async () => {
    // if (window.confirm("Sei sicuro di voler eliminare la lista?")) {
      ApiManager.Top.deletePickingList(
        pickingListIdToDelete,
        (data) => {
          fetchData();
        },
        (error) => {
          console.error("Error deleting picking list:", error);
        },
        () => {
          setPickingListIdToDelete(0);
          setShowDeleteConfirmation(false); // Close modal after confirmation
        }
      );
    // }
  };

  const handleCloseModal = async (data) => {
    setSelectedPickingListId(0);
    await fetchData();
  };

  const containerClass = useMemo(
    () =>
      movReason === -1
        ? "d-flex justify-content-between align-items-center"
        : "d-flex justify-content-center align-items-center",
    [movReason]
  );

  return (
    <div className="date-range-filter">
      <Row className="mb-3">
        <Col>
          <Form.Label htmlFor="movReasonSelect">Tipo di Movimento</Form.Label>
          <Form.Select
            id="movReasonSelect"
            value={movReason}
            onChange={(e) => setMovReason(parseInt(e.target.value))}
            onKeyDown={handleKeyDown}
          >
            <option value={1}>Carichi</option>
            <option value={-1}>Scarichi</option>
          </Form.Select>
        </Col>
        <Col>
          <Form.Label htmlFor="startDateInput">Da data</Form.Label>
          <InputGroup className="mb-3">
            {startDate && (
              <Button
                title="Cancella"
                variant="outline-secondary"
                id="button-addon3"
                onClick={handleClearStartDate}
              >
                <ClearIcon />
              </Button>
            )}
            <FormControl
              type="date"
              id="startDateInput"
              aria-label="Start date"
              aria-describedby="button-addon2"
              value={startDate}
              onChange={handleStartDateChange}
              onKeyDown={handleKeyDown}
            />
          </InputGroup>
        </Col>
        <Col
          xs="auto"
          className="d-flex align-items-center justify-content-center"
        >
          <Button
            variant="outline-secondary"
            id="button-addon4"
            onClick={handleSameDate}
            className="mt-2"
          >
            =
          </Button>
        </Col>
        <Col>
          <Form.Label htmlFor="endDateInput">A data</Form.Label>
          <InputGroup className="mb-3">
            {endDate && (
              <Button
                title="Cancella"
                variant="outline-secondary"
                id="button-addon5"
                onClick={handleClearEndDate}
              >
                <ClearIcon />
              </Button>
            )}
            <FormControl
              type="date"
              id="endDateInput"
              placeholder="A data"
              aria-label="End date"
              aria-describedby="button-addon2"
              value={endDate}
              onChange={handleEndDateChange}
              onKeyDown={handleKeyDown}
              />
          </InputGroup>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col className="mb-3 text-end">
          <Button
            title="Oggi"
            id="buttonToday"
            variant="primary"
            onClick={setToday}
          >
            <TodayIcon />
          </Button>
          &nbsp;
          <Button
            title="Cerca"
            variant="primary"
            id="buttonSearch"
            onClick={handleSearchClick}
            disabled={!startDate || !endDate}
            >
            <SearchIcon />
          </Button>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <RowCountLabel count={data?.length} label="Liste" />
          {data?.map((row, index) => (
            <React.Fragment key={`row-${row.id}`}>
              <div className={containerClass}>
                {movReason === -1 && (
                  <Button
                    title="Modifica"
                    variant="primary"
                    onClick={() => handleEditModal(row.id)}
                    className="float-start"
                  >
                    <EditIcon />
                  </Button>
                )}
                <h5 className="text-center">
                  {`Lista ${row.docId}`}
                  <br />
                  <span style={{ fontSize: "10px", opacity: "0.5" }}>
                    {row.id}
                  </span>
                </h5>
                {movReason === -1 && (
                  <Button
                    title="Elimina"
                    variant="danger"
                    onClick={() => handleDeletePickingList(row.id)}
                    className="float-end"
                  >
                    <DeleteForeverIcon />
                  </Button>
                )}
              </div>
              <Table striped>
                <thead>
                  <tr>
                    <th>Documento</th>
                    <th>Articolo</th>
                    <th>Fornitore</th>
                    <th>Cliente</th>
                    <th className="text-end" style={{ width: "100px" }}>
                      Anno
                    </th>
                    <th className="text-end" style={{ width: "170px" }}>
                      Qta
                    </th>
                    <th>Ubicazione</th>
                  </tr>
                </thead>
                <tbody>
                  {row?.cellProducts?.map((rowCp, rowCpIndex) => (
                    <tr
                      key={`${row.id}-${rowCp.id}`}
                      style={{ verticalAlign: "middle" }}
                    >
                      <td style={{ width: "20%" }}>
                        <ul className="list-unstyled">
                          <li>
                            {rowCp.docTypeDescr}&nbsp;
                            {rowCp.docNumb}
                            {rowCp.docSeries?.trim()
                              ? "/" + rowCp.docSeries
                              : ""}
                            &nbsp;
                            {DataManager.Date.format(
                              rowCp.docDate,
                              "dd/MM/yyyy"
                            )}
                          </li>
                          <li>
                            <span className="half-parent-text">
                              {rowCp.transpReasonDescr}
                            </span>
                          </li>
                        </ul>
                      </td>
                      <td style={{ width: "20%" }}>
                        <ProductLink {...rowCp} showProductLink={false} />
                      </td>
                      <td style={{ width: "20%" }}>
                        <ul className="list-unstyled">
                          <li>{rowCp.supplierCode}</li>
                          <li>{rowCp.supplierDescr}</li>
                        </ul>
                      </td>
                      <td style={{ width: "20%" }}>
                        <ul className="list-unstyled">
                          <li>{rowCp.customerCode}</li>
                          <li>{rowCp.customerDescr}</li>
                        </ul>
                      </td>
                      <td style={{ width: "100px" }} className="text-end">
                        {rowCp.year}
                      </td>
                      <td style={{ width: "170px" }} className="text-end">
                        {rowCp.amount}
                      </td>
                      <td>{rowCp.location}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </React.Fragment>
          ))}
        </Col>
      </Row>

      {/* Modal per confermare la cancellazione */}
      <Modal
        show={showDeleteConfirmation}
        onHide={() => setShowDeleteConfirmation(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Conferma cancellazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Sei sicuro di voler eliminare questa picking list?
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="danger" onClick={confirmDeletePickingList}>
            Elimina
          </Button>
          <Button variant="secondary" onClick={() => setShowDeleteConfirmation(false)}>
            Annulla
          </Button>
        </Modal.Footer>
      </Modal>

      <ModalEditOutgoings
        pickingListId={selectedPickingListId}
        movements={
          data?.find((pList) => pList.id === selectedPickingListId)
            ?.cellProducts
        }
        showModal={showModalEditOutgoings}
        onCloseModal={handleCloseModal}
      />
    </div>
  );
};

export default PickingLists;
